import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Input from "sushiweb/InputTypes/Input";
import Button from "sushiweb/Button";
import Textarea from "sushiweb/InputTypes/Textarea";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Checkbox from "sushiweb/InputTypes/Checkbox";

import {
  isValidated,
  getInitialState,
  filterObj,
  getPath,
} from "../../../utils";
import * as PolygonActions from "../../../store/actions/Polygon";
import * as CityActions from "../../../store/actions/City";
import * as UserActions from "../../../store/actions/User";
import InfiniteScrollDropdown from "../../Common/InfiniteScrollDropdown";
import Permission from "../../Common/Permission";
import MenuNames from "../../../constants/Menu/MenuNames.json";
import MenuActionsMappings from "../../../constants/Menu/MenuActionsMappings";
import {
  BottomSectionModal,
  FlexContainer,
} from "../../../constants/assets/containers";
import NoFooterModal from "../../Common/NoFooterModal";
import variables from "../../../constants/assets/variables";

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  fieldsMargin: {
    margin: "10px 0",
  },
  errorSpacing: {
    marginTop: "25px",
  },
}));

const mandatoryFields = ["polygon.polygon_name", "polygon.city_id", "polygon.centroid", "polygon.polygon_type", "boundary", "polygon.ref_id", "polygon.zone_id"];
const editableFields = [
  "polygon.polygon_name",
  "polygon.centroid",
  "polygon.is_active",
  "polygon.zone_id",
  "boundary",
];

const addEditableFields = [
  "polygon.polygon_name",
  "polygon.city_id",
  "polygon.polygon_type",
  "polygon.centroid",
  "polygon.ref_id",
  "polygon.is_active",
  "polygon.zone_id",
  "boundary",
];

const PolygonFormModal = (props) => {
  const classes = useStyles();
  const allActions = MenuActionsMappings[MenuNames.POLYGON];
  const [initialState, setInitialState] = useState({
    "polygon.polygon_name": "",
    "polygon.city_id": "",
    "polygon.polygon_type": "",
    "polygon.centroid": "",
    "polygon.ref_id": "",
    "polygon.is_active": false,
    "polygon.zone_id": "",
    boundary: "",
  });
  const {
    modalState,
    handleClose,
    activeRefId,
    activePolygon,
    activeFilters,
    filterFields,
    setStatus,
    currentPage,
  } = props;

  function postProcessor(body) {
    if (typeof body === "object") {
      const toCheckVal = body['polygon.centroid'];
      if (typeof toCheckVal === 'object') {
        body['polygon.centroid'] = JSON.stringify(toCheckVal);
      } else if (typeof toCheckVal === 'string') {
        body['polygon.centroid'] = `[${JSON.parse(toCheckVal).toString()}]`;
      }
    }

    return body;
  }

  const handleSubmit = (evt) => {
    evt.preventDefault();
    // if filter button is clicked, then apply filters
    if (modalState === "Filter") {
      const filters = {};
      for (let key of filterFields) {
        if (initialState[key]) {
          filters[key] = initialState[key];
        }
      }
      props.getPolygonList(filters, {}).then((res) => {
        handleClose();
      });
    } else {
      const body = filterObj(initialState, addEditableFields);
      let validatedState = isValidated(body, mandatoryFields);

      if (validatedState === "validated") {
        // boundary should be in correct array format.
        // we test for correct JSON here which will produce the
        // desired test.
        if (getPath(body, "boundary.length", 0) > 0) {
          try {
            const boundary = JSON.parse(body.boundary);
            if (Array.isArray(boundary) && boundary.length === 0) {
              throw new Error("Boundary is an empty array");
            }
          } catch (err) {
            validatedState =
              "Boundary is not in correct format. Sample value: [[12.957212, 77.616819],[13.958689, 14.6209],[12.957212, 77.616819]]";
            if (err.message === "Boundary is an empty array") {
              validatedState = "Boundary is an empty array";
            }
          }
        } else {
          body.boundary = "";
        }

        // centroid should be in correct array format.
        // we test for correct JSON here which will produce the
        // desired test.
        if (body?.["polygon.centroid"]) {
          try {
            const toCheckVal = body['polygon.centroid'];
            let centroid = [];

            if (typeof toCheckVal === 'string') {
              centroid = JSON.parse(body["polygon.centroid"]);
            }
            else if (Array.isArray(toCheckVal)) {
              centroid = toCheckVal;
            }

            if (centroid.length === 0) {
              throw new Error("Centroid is an empty array");
            }
            if (centroid.length !== 2) {
              throw new Error("Centroid should have 2 values only. Format: [latitude, longitude]");
            }
          } catch (err) {
            validatedState =
              "Centroid is not in correct format. Sample value: [12.957212, 77.616819]";
            if (err.message === "Centroid is an empty array") {
              validatedState = "Centroid is an empty array";
            }
            if (err.message === "Centroid should have 2 values only. Format: [latitude, longitude]") {
              validatedState = "Centroid should have 2 values only. Format: [latitude, longitude]";
            }
          }
        }

         // zoneId should be an integer
         const zoneId = parseInt(body["polygon.zone_id"], 10);
         if (isNaN(zoneId)) {
           validatedState = "ZoneID must be an integer.";
         } else {
           body["polygon.zone_id"] = zoneId;
         }

      } else {
        setStatus({ type: "error", message: validatedState });
      }

      if (validatedState === "validated") {
        if (modalState === "Edit") {
          const processedPayload = postProcessor(body);
          props.updatePolygon(activeRefId, processedPayload).then((res) => {
            const pageRequest = { page: currentPage };
            props.getPolygonList(activeFilters, pageRequest);
            handleClose();
          });
        } else if (modalState === "Add") {
          props.createPolygon(body).then((res) => {
            const pageRequest = { page: currentPage };
            props.getPolygonList(activeFilters, pageRequest);
            handleClose();
          });
        }
      } else {
        setStatus({ type: "error", message: validatedState });
      }
    }
  };

  // clears all applied filters.
  const handleClearFilters = () => {
    props.getPolygonList({}).then((res) => {
      props.resetState({ state: "activeFilters" });
      handleClose();
    });
  };

  const handleChange = (key, val) => {
    const values = { ...initialState };
    values[key] = val;
    
    setInitialState(values);
  };

  useEffect(() => {
    if (activeRefId) {
      props.getPolygon(activeRefId);
    }

    // reset the state to show empty modal
    if (modalState === "Add" && Object.keys(activePolygon).length > 0) {
      props.resetState({ state: "activePolygon" });
    }
  }, [activeRefId, modalState]);

  useEffect(() => {
    // update the initialState on modal render.
    // this is not needed when modalState is "Add".
    if (modalState === "Edit" && Object.keys(activePolygon).length > 0) {
      const values = { ...initialState };
      Object.keys(values).map((key) => {
        values[key] = activePolygon[key]
          ? activePolygon[key]
          : getInitialState(initialState, key);
      });

      setInitialState(values);
    } else if (
      Object.keys(activeFilters).length > 0 &&
      modalState === "Filter"
    ) {
      const values = { ...initialState };
      Object.keys(values).map((key) => {
        values[key] = activeFilters[key]
          ? activeFilters[key]
          : getInitialState(initialState, key);
      });
      setInitialState(values);
    }
  }, [activePolygon, activeFilters]);

  const isDisabledField = (value) => {
    if (modalState === "Edit") {
      if (editableFields.includes(value)) {
        return false;
      }
      return true;
    } else if (modalState === "Filter") {
      if (filterFields.includes(value)) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  };

  const getRequiredButtonPerms = () => {
    if (modalState === "Edit") {
      return [allActions.UPDATE];
    } else if (modalState === "Add") {
      return [allActions.CREATE];
    }
    return undefined;
  };

  const isHiddenField = (key) => {
    if (modalState === "Filter") {
      return !filterFields.includes(key);
    }
    return false;
  };

  const initialValueCentroid = initialState["polygon.centroid"];
  const flattenedCentroidValue = Array.isArray(initialValueCentroid) ? `[${initialValueCentroid.toString()}]` : initialValueCentroid;

  const formElements = [
    {
      key: "polygon.polygon_name",
      element: (
        <Input
          value={initialState["polygon.polygon_name"]}
          disabled={isDisabledField("polygon.polygon_name")}
          data-testid="polygonName"
          id="sushi-input"
          onChange={(e) => handleChange("polygon.polygon_name", e)}
          label="Name"
        />
      ),
    },
    {
      key: "polygon.polygon_type",
      element: (
        <InfiniteScrollDropdown
          value={{
            polygon_type: initialState?.["polygon.polygon_type"],
          }}
          priorityFlag={1}
          id="polygonTypeDropdown"
          getActiveList={props.getPolygonTypes}
          resKey="polygon"
          modalState={modalState}
          keyName="polygon.polygon_type"
          labelKey="polygon_type"
          getDataLabelKey="polygon.polygon_type"
          keyTypeToSet="polygon_type"
          disabled={isDisabledField("polygon.polygon_type")}
          label="Type"
          handleChange={handleChange}
          remoteSearchApplicable
          activeFilters={activeFilters}
          activeElement={activePolygon}
          appliedFilters={{}}
          remoteSearchKey="startswith"
        />
      ),
    },
    {
      key: "polygon.city_id",
      element: (
        <InfiniteScrollDropdown
          priorityFlag={1}
          id="polygonCityDropdown"
          getActiveList={props.getCityList}
          nextPage={props.nextPageCity}
          resKey="cities"
          modalState={modalState}
          keyName="polygon.city_id"
          labelKey="city_name"
          getDataLabelKey="city.city_name"
          keyTypeToSet="id"
          disabled={isDisabledField("polygon.city_id")}
          label="City"
          handleChange={handleChange}
          remoteSearchApplicable
          activeFilters={activeFilters}
          activeElement={activePolygon}
          getActiveItemById={props.getCity}
          appliedFilters={{}}
          remoteSearchKey="startswith"
        />
      ),
    },
    {
      key: "polygon.zone_id",
      element: (
        <Input
          value={initialState["polygon.zone_id"]}
          disabled={isDisabledField("polygon.zone_id")}
          data-testid="zoneId"
          id="sushi-input"
          onChange={(e) => handleChange("polygon.zone_id", e)}
          label="ZoneID"
        />
      ),
    },
    {
      key: "polygon.centroid",
      element: (
        <Input
          value={flattenedCentroidValue}
          disabled={isDisabledField("polygon.centroid")}
          data-testid="centroid"
          id="sushi-input"
          onChange={(e) => handleChange("polygon.centroid", e)}
          label="Centroid"
        />
      ),
    },
    {
      key: "polygon.ref_id",
      element: (
        <Input
          value={initialState["polygon.ref_id"]}
          disabled={isDisabledField("polygon.ref_id")}
          data-testid="refId"
          id="sushi-input"
          onChange={(e) => handleChange("polygon.ref_id", e)}
          label="Reference ID"
        />
      ),
    },
    {
      key: "checkbox-form-group",
      element: (
        <FlexContainer style={{ flexWrap: "wrap" }} className="mb-20">
          <Checkbox
            disabled={isDisabledField("polygon.is_active")}
            value="polygonActive"
            color={variables.red500}
            checked={initialState["polygon.is_active"]}
            onChange={(e) =>
              handleChange("polygon.is_active", e.polygonActive)
            }
            className="sushi-checkbox"
            data-testid="polygonActive"
            label={"Is Active"}
          />
        </FlexContainer>
      ),
    },
    {
      key: "boundary",
      element: (
        <Textarea
          id="sushi-textarea"
          data-testid="polygonBoundary"
          borderColor={variables.primaryColor}
          innerWidth="100%"
          className="mb-20"
          rows={4}
          resize="none"
          label="Boundary"
          disabled={isDisabledField("boundary")}
          onChange={(e) => handleChange("boundary", e)}
          value={initialState.boundary}
        />
      ),
    },
  ];

  return (
    <NoFooterModal
      header={true}
      heading={`${modalState} Polygon`}
      handleClose={handleClose}
      id="PolygonFormModal"
      modalButtons={
        <BottomSectionModal className="flex items-center justify-between">
          {/* show the clear button when clicked on filters button */}
          {modalState === "Filter" && (
            <Button
              data-testid="filterFeatureBtn"
              onClick={handleClearFilters}
              className="sushi-btn"
            >
              Clear
            </Button>
          )}
          <Permission requiredPerms={getRequiredButtonPerms()}>
            <Button
              data-testid="submitButton"
              className="sushi-btn"
              onClick={handleSubmit}
            >
              {modalState === "Filter" ? "Apply" : "Submit"}
            </Button>
          </Permission>
        </BottomSectionModal>
      }
    >
      {formElements
        .filter((obj) => !isHiddenField(obj.key))
        .map((obj) => obj.element)}
    </NoFooterModal>
  );
};

const mapStateToProps = (state) => {
  const { Polygon, City } = state;
  return {
    activePolygon: Polygon.activePolygon,
    activeFilters: Polygon.activeFilters,
    currentPage: Polygon.currentPage,
    nextPageCity: City.nextPage,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    createPolygon: bindActionCreators(
      PolygonActions.createPolygon,
      dispatch
    ),
    getPolygonList: bindActionCreators(
      PolygonActions.getPolygonList,
      dispatch
    ),
    getPolygon: bindActionCreators(PolygonActions.getPolygon, dispatch),
    getPolygonTypes: bindActionCreators(
      PolygonActions.getPolygonTypes,
      dispatch
    ),
    resetState: bindActionCreators(PolygonActions.resetState, dispatch),
    updatePolygon: bindActionCreators(
      PolygonActions.updatePolygon,
      dispatch
    ),
    getCityList: bindActionCreators(CityActions.getCityList, dispatch),
    getCity: bindActionCreators(CityActions.getCity, dispatch),
    setStatus: bindActionCreators(UserActions.setStatus, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PolygonFormModal);
